/**
 * Central registry mapping Win32 registry keys to their SVG files and Fluent UI fallback icon.
 *
 * To add a new icon:
 *   1. Add an entry here with svgFile and fluentFallback
 *   2. Run: node packages/common/controls/packages/controls/owa-command-ribbon-win32-icon/scripts/generateWin32Icons.mjs
 *
 * svgFile: base file name in node_modules/@1js/icons.monoline/x-none/; used for all sizes unless overridden
 * defaultSvgSize: optional source size override for ACUI's default icon slot
 * smallSvgFile/largeSvgFile: optional artwork overrides for ACUI's icon16/icon32 slots
 * largeSvgSize: optional source size override for ACUI's icon32 slot
 * fluentFallback: the string returned by Fluent or ribbon brand icon imports
 */
interface Win32IconEntry {
    svgFile: string;
    defaultSvgSize?: 16 | 20 | 32;
    smallSvgFile?: string;
    largeSvgFile?: string;
    largeSvgSize?: 16 | 20 | 32;
    fluentFallback: string;
}

export type Win32IconRegistryKey = "AddWin32" | "AllAppsWin32" | "ArchiveWin32" | "ArrowDownWin32" | "ArrowUpWin32" | "AssignPolicyWin32" | "BlockWin32" | "CategorizeWin32" | "CollapseConversationWin32" | "ComposeWin32" | "NewFolderWin32" | "NewSearchFolderWin32" | "SendReceiveAllFoldersWin32" | "WorkOfflineWin32" | "ContactSupportWin32" | "ConversationsWin32" | "DayViewWin32" | "DeleteWin32" | "DensityWin32" | "DiscoverGroupsWin32" | "ExpandConversationWin32" | "FeedbackWin32" | "FilterWin32" | "FocusedInboxWin32" | "FollowUpWin32" | "FolderPermissionsWin32" | "FolderPaneWin32" | "ForwardAsAttachmentWin32" | "ForwardWin32" | "GetDiagnosticsWin32" | "HelpWin32" | "IgnoreWin32" | "ImmersiveReaderWin32" | "FavoritesWin32" | "InstantMessageWin32" | "LayoutWin32" | "MeetingWin32" | "MessagePreviewWin32" | "MonthViewWin32" | "MoreAppsWin32" | "MoveWin32" | "NewEventWin32" | "NewItemsWin32" | "NextSevenDaysWin32" | "NotificationsWin32" | "PrintWin32" | "QrCodeWin32" | "ReadingPaneBottomWin32" | "ReadingPaneOffWin32" | "ReadingPaneRightWin32" | "ReadingPaneWin32" | "RedoWin32" | "RemindersWindowWin32" | "ReplyAllWin32" | "ReplyWin32" | "ReportWin32" | "RulesWin32" | "SaveWin32" | "SearchWin32" | "ShareToTeamsWin32" | "ShareWin32" | "SplitViewWin32" | "TimeScaleWin32" | "TipsWin32" | "ToDoBarWin32" | "TodayWin32" | "UndoWin32" | "ViewSettingsWin32" | "WeekViewWin32" | "WorkLocationWin32" | "WorkWeekViewWin32";

export const win32IconRegistrations: Record<Win32IconRegistryKey, Win32IconEntry> = {
    AddWin32: {
        svgFile: "plus_owa",
        fluentFallback: "AddRegular",
    },
    AllAppsWin32: {
        svgFile: "fluentappfolder",
        fluentFallback: "AppFolderRegular",
    },
    ArchiveWin32: {
        svgFile: "archive_dol",
        fluentFallback: "ArchiveRegular",
    },
    ArrowDownWin32: {
        svgFile: "arrowdown",
        fluentFallback: "ArrowDownRegular",
    },
    ArrowUpWin32: {
        svgFile: "arrowup",
        fluentFallback: "ArrowUpRegular",
    },
    AssignPolicyWin32: {
        svgFile: "archive",
        fluentFallback: "ArchiveSettingsRegular",
    },
    BlockWin32: {
        svgFile: "addtojunksenderslistribbon",
        fluentFallback: "PersonProhibitedRegular",
    },
    CategorizeWin32: {
        svgFile: "appointmentcolorpopup",
        fluentFallback: "TagRegular",
    },
    CollapseConversationWin32: {
        svgFile: "collapseall",
        fluentFallback: "TextCollapseRegular",
    },
    ComposeWin32: {
        svgFile: "newmail",
        fluentFallback: "ComposeRegular",
    },
    ContactSupportWin32: {
        svgFile: "accountsupport",
        fluentFallback: "PersonSupportRegular",
    },
    ConversationsWin32: {
        svgFile: "olconvounreaditemsandreaditems",
        fluentFallback: "MailMultipleRegular",
    },
    DayViewWin32: {
        svgFile: "dailyview",
        fluentFallback: "CalendarDayRegular",
    },
    DeleteWin32: {
        svgFile: "delete_owa",
        fluentFallback: "DeleteRegular",
    },
    DensityWin32: {
        svgFile: "linespacing",
        fluentFallback: "TextDensityRegular",
    },
    NewFolderWin32: {
        svgFile: "newfolder",
        fluentFallback: "FolderRegular",
    },
    NewSearchFolderWin32: {
        svgFile: "searchfolder_dol",
        fluentFallback: "FolderSearchRegular",
    },
    SendReceiveAllFoldersWin32: {
        svgFile: "jotsyncsyncingtoolbar_metro",
        fluentFallback: "ArrowRepeatAllRegular",
    },
    WorkOfflineWin32: {
        svgFile: "clvofflineconnection",
        fluentFallback: "CloudOffRegular",
    },
    DiscoverGroupsWin32: {
        svgFile: "browsegroups",
        fluentFallback: "PeopleCommunityRegular",
    },
    ExpandConversationWin32: {
        svgFile: "expandall",
        fluentFallback: "TextExpandRegular",
    },
    FeedbackWin32: {
        svgFile: "feedback",
        fluentFallback: "PersonFeedbackRegular",
    },
    FilterWin32: {
        svgFile: "filter",
        fluentFallback: "FilterRegular",
    },
    FocusedInboxWin32: {
        svgFile: "focusedinbox",
        fluentFallback: "MailInboxCheckmarkRegular",
    },
    FollowUpWin32: {
        svgFile: "olflagtoday",
        fluentFallback: "FlagRegular",
    },
    FolderPermissionsWin32: {
        svgFile: "securityoptionsmenu",
        fluentFallback: "FolderPeopleRegular",
    },
    FolderPaneWin32: {
        svgFile: "menunavigationpane",
        fluentFallback: "PanelLeftFilled",
    },
    ForwardAsAttachmentWin32: {
        svgFile: "sendtomailrecipient",
        fluentFallback: "MailAttachRegular",
    },
    ForwardWin32: {
        svgFile: "olforwardedmail",
        smallSvgFile: "forwardarrow",
        fluentFallback: "ArrowForwardRegular",
    },
    GetDiagnosticsWin32: {
        svgFile: "repairbutton",
        defaultSvgSize: 16,
        fluentFallback: "ToolboxRegular",
    },
    HelpWin32: {
        svgFile: "question",
        fluentFallback: "QuestionRegular",
    },
    IgnoreWin32: {
        svgFile: "ignoreconversation",
        fluentFallback: "SpeakerMuteRegular",
    },
    ImmersiveReaderWin32: {
        svgFile: "audioreader",
        fluentFallback: "ImmersiveReaderRegular",
    },
    InstantMessageWin32: {
        svgFile: "personamenusendim",
        fluentFallback: "ChatRegular",
    },
    LayoutWin32: {
        svgFile: "menunavigationpane",
        fluentFallback: "PanelLeftRegular",
    },
    MeetingWin32: {
        svgFile: "replywithmeeting",
        largeSvgSize: 20,
        fluentFallback: "CalendarReplyRegular",
    },
    MessagePreviewWin32: {
        svgFile: "messagequickpreview",
        fluentFallback: "MailListRegular",
    },
    MonthViewWin32: {
        svgFile: "monthlyview",
        fluentFallback: "CalendarLtrRegular",
    },
    MoreAppsWin32: {
        svgFile: "fluentappfolder",
        fluentFallback: "AddSquareRegular",
    },
    MoveWin32: {
        svgFile: "movetofolder",
        fluentFallback: "FolderArrowRightRegular",
    },
    NewEventWin32: {
        svgFile: "newappointment",
        fluentFallback: "CalendarEmptyRegular",
    },
    NewItemsWin32: {
        svgFile: "mailnewitemmenu",
        fluentFallback: "CalendarMailRegular",
    },
    NextSevenDaysWin32: {
        svgFile: "nextsevendays",
        fluentFallback: "CalendarArrowRightRegular",
    },
    NotificationsWin32: {
        svgFile: "reminder",
        fluentFallback: "AlertRegular",
    },
    FavoritesWin32: {
        svgFile: "favorites",
        fluentFallback: "StarAddRegular",
    },
    PrintWin32: {
        svgFile: "print",
        fluentFallback: "PrintRegular",
    },
    QrCodeWin32: {
        svgFile: "mobilephone",
        fluentFallback: "PhoneRegular",
    },
    ReadingPaneBottomWin32: {
        svgFile: "previewbottom",
        fluentFallback: "PanelBottomRegular",
    },
    ReadingPaneOffWin32: {
        svgFile: "previewoff",
        fluentFallback: "WindowRegular",
    },
    ReadingPaneRightWin32: {
        svgFile: "previewright",
        fluentFallback: "PanelRightRegular",
    },
    ReadingPaneWin32: {
        svgFile: "menupreviewpane",
        fluentFallback: "PanelLeftFocusRightFilled",
    },
    RedoWin32: {
        svgFile: "redo",
        fluentFallback: "ArrowRedoRegular",
    },
    RemindersWindowWin32: {
        svgFile: "showreminderswindow",
        fluentFallback: "ClockAlarmRegular",
    },
    ReplyAllWin32: {
        svgFile: "replyall",
        fluentFallback: "ArrowReplyAllRegular",
    },
    ReplyWin32: {
        svgFile: "replycurvedarrow",
        largeSvgFile: "respond",
        fluentFallback: "ArrowReplyRegular",
    },
    ReportWin32: {
        svgFile: "reportphishing",
        fluentFallback: "ShieldErrorRegular",
    },
    RulesWin32: {
        svgFile: "createmailrule",
        fluentFallback: "FolderMailRegular",
    },
    SaveWin32: {
        svgFile: "save",
        fluentFallback: "SaveRegular",
    },
    SearchWin32: {
        svgFile: "outlooksearchmagnifyingglass",
        fluentFallback: "SearchRegular",
    },
    ShareToTeamsWin32: {
        svgFile: "teamsmonoline",
        fluentFallback: "TeamsColor",
    },
    ShareWin32: {
        svgFile: "shareglyph",
        fluentFallback: "ShareRegular",
    },
    SplitViewWin32: {
        svgFile: "overlaycalendar",
        fluentFallback: "CalendarMultipleRegular",
    },
    TimeScaleWin32: {
        svgFile: "dateandtime",
        fluentFallback: "AutoFitHeightRegular",
    },
    TipsWin32: {
        svgFile: "lightbulb",
        fluentFallback: "LightbulbRegular",
    },
    ToDoBarWin32: {
        svgFile: "menutodobar",
        fluentFallback: "CalendarTodoRegular",
    },
    TodayWin32: {
        svgFile: "today",
        fluentFallback: "CalendarTodayRegular",
    },
    UndoWin32: {
        svgFile: "undo",
        fluentFallback: "ArrowUndoRegular",
    },
    ViewSettingsWin32: {
        svgFile: "currentviewsettings",
        fluentFallback: "SettingsRegular",
    },
    WeekViewWin32: {
        svgFile: "weeklyview",
        fluentFallback: "Calendar3DayRegular",
    },
    WorkLocationWin32: {
        svgFile: "buildinghome",
        fluentFallback: "BuildingRegular",
    },
    WorkWeekViewWin32: {
        svgFile: "appt_workweekview",
        fluentFallback: "CalendarWorkWeekRegular",
    },
};

/** Fluent icon string name → Win32 registry keys for ACUI's small, default, and large slots. */
export const fluentToWin32Map: Readonly<Record<string, Readonly<{
    small: string;
    default: string;
    large: string;
}>>> = Object.fromEntries(Object.entries(win32IconRegistrations).map(([win32Key, { fluentFallback }]) => [
    fluentFallback,
    {
        small: `${win32Key}16px`,
        default: `${win32Key}20px`,
        large: `${win32Key}32px`,
    },
]));
